// components/ShopHeader.tsx
import { useEffect, useState } from "react";
import { FaBars, FaTimes, FaSearch, FaHeart, FaShoppingBag, FaBox } from "react-icons/fa";

export interface NavItem {
  label: string;
  target: string;
}

interface ShopHeaderProps {
    darkMode?: boolean;
  links?: NavItem[];
  homeSection?: string;
  logo?: React.ReactNode;
  siteName?: string;
  cartItemCount?: number;
  onClickHome?: () => void;
  onCartClick?: () => void;
  onOrdersClick?: () => void;
  onSearchClick?: () => void;
  onWishlistClick?: () => void;
  isLoggedIn?: boolean;
  onLoginClick?: () => void;
  onLogoutClick?: () => void;
  user?: any;
}

const DEFAULT_LINKS: NavItem[] = [
  { label: "Home", target: "home" },
  { label: "Products", target: "products" },
  { label: "Reviews", target: "testimonials" },
  { label: "Contact", target: "contact" },
];

export default function ShopHeader({
  logo,
  darkMode,
  siteName,
  links = DEFAULT_LINKS,
  homeSection = "home",
  cartItemCount = 0,
  onClickHome,
  onCartClick,
  onOrdersClick,
  onSearchClick,
  onWishlistClick,
  isLoggedIn = true,
  onLoginClick,
  onLogoutClick,
}: ShopHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(darkMode || false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(homeSection);


  const navLinks = links.length ? links : DEFAULT_LINKS;

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Intersection Observer for active section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible?.target.id) {
          setActiveSection(visible.target.id);
        }
      },
      {
        threshold: 0.35,
        rootMargin: "-100px 0px -100px 0px",
      }
    );

    navLinks.forEach((link) => {
      const element = document.getElementById(link.target);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [navLinks]);

  // Handle hash navigation on mount
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      }
    }
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (!element) return;

    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    window.history.replaceState(null, "", `#${sectionId}`);
    setActiveSection(sectionId);
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6">
        {/* Logo */}
        <button
          type="button"
          onClick={() => {
            onClickHome;
            scrollToSection(homeSection)}}
          className={`flex items-center gap-2 shrink-0 transition-transform duration-200 hover:scale-105 ${
            isScrolled ? "text-gray-800" : "text-white"
          }`}
          aria-label="Go to Home"
        >
          {logo}
          {siteName && (
            <span className="font-primary text-xl font-bold">
              {siteName}
            </span>
          )}
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8 lg:gap-10 px-3">
          {links.map((item) => {
            const active = activeSection === item.target;
            return (
              <button
                key={item.target}
                type="button"
                onClick={() => scrollToSection(item.target)}
                className={`relative text-[13px] lg:text-[15px] uppercase tracking-[1px] lg:tracking-[3px] font-medium transition-all duration-200 ${
                  active
                    ? "text-blue-600 font-semibold"
                    : isScrolled
                    ? "text-gray-700"
                    : "text-white/90"
                } hover:text-blue-500`}
              >
                {item.label}
                {active && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full bg-blue-600" />
                )}
              </button>
            );
          })}
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-3">
          {/* Search */}
          <button
            onClick={onSearchClick}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              isScrolled
                ? "text-gray-600 hover:bg-gray-100"
                : "text-white/80 hover:text-white hover:bg-white/10"
            }`}
            aria-label="Search"
          >
            <FaSearch className="text-lg" />
          </button>

          {/* Wishlist */}
          <button
            onClick={onWishlistClick}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              isScrolled
                ? "text-gray-600 hover:bg-gray-100"
                : "text-white/80 hover:text-white hover:bg-white/10"
            }`}
            aria-label="Wishlist"
          >
            <FaHeart className="text-lg" />
          </button>

          {/* Orders */}
          <button
            onClick={onOrdersClick}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              isScrolled
                ? "text-gray-600 hover:bg-gray-100"
                : "text-white/80 hover:text-white hover:bg-white/10"
            }`}
            aria-label="Orders"
          >
            <FaBox className="text-lg" />
          </button>

          {/* Cart */}
          <button
            onClick={onCartClick}
            className={`p-2 rounded-lg transition-colors duration-200 relative ${
              isScrolled
                ? "text-gray-600 hover:bg-gray-100"
                : "text-white/80 hover:text-white hover:bg-white/10"
            }`}
            aria-label="Cart"
          >
            <FaShoppingBag className="text-lg" />
            {cartItemCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {cartItemCount > 9 ? "9+" : cartItemCount}
              </span>
            )}
          </button>

          {/* Auth */}
          <button
            onClick={isLoggedIn ? onLogoutClick : onLoginClick}
            className={`hidden sm:block px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
              isScrolled
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border border-white/30"
            }`}
          >
            {isLoggedIn ? "Logout" : "Login"}
          </button>

          {/* Mobile Menu Button */}
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            className="md:hidden text-2xl"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? (
              <FaTimes className={isScrolled ? "text-gray-800" : "text-white"} />
            ) : (
              <FaBars className={isScrolled ? "text-gray-800" : "text-white"} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed inset-x-0 top-[72px] bg-white shadow-2xl transition-all duration-300 ${
          isMobileMenuOpen
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-4 pointer-events-none"
        }`}
      >
        <nav className="flex flex-col p-6 space-y-4">
          {links.map((item) => {
            const active = activeSection === item.target;
            return (
              <button
                key={item.target}
                type="button"
                onClick={() => scrollToSection(item.target)}
                className={`text-left py-2 uppercase tracking-[2px] border-b border-gray-100 transition-colors ${
                  active
                    ? "text-blue-600 border-blue-600 font-semibold"
                    : "text-gray-700 hover:text-blue-600"
                }`}
              >
                {item.label}
              </button>
            );
          })}
          <div className="pt-4 border-t border-gray-200 flex flex-col gap-3">
            <button
              onClick={isLoggedIn ? onLogoutClick : onLoginClick}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors duration-200"
            >
              {isLoggedIn ? "Logout" : "Login"}
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
}